import { useCreateEmployee } from './useCreateEmployee';
import { useUpdateEmployee } from './useUpdateEmployee';

/**
 * Hook for employee mutation operations (create, update)
 *
 * Provides centralized access to employee mutation functions with proper port injection.
 * Eliminates the need to pass repository ports through component props.
 * Uses React Query mutations for optimistic updates and cache invalidation.
 *
 * @returns {Object} Mutation functions
 * @returns {Function} returns.createEmployee - Function to create new employee
 * @returns {Function} returns.updateEmployee - Function to update existing employee
 *
 * @example
 * const { createEmployee, updateEmployee } = usePersonnelMutations();
 *
 * const handleSubmit = async (data) => {
 *   if (mode === 'edit') {
 *     await updateEmployee({ userUuid: id, ...data });
 *   } else {
 *     await createEmployee(data);
 *   }
 * };
 *
 * @example
 * // With error handling
 * const { createEmployee, updateEmployee } = usePersonnelMutations();
 *
 * try {
 *   await createEmployee(formData);
 *   toast.success('Employee created successfully');
 * } catch (error) {
 *   toast.error('Failed to create employee');
 * }
 */
export const usePersonnelMutations = () => {
  const { mutateAsync: createEmployee } = useCreateEmployee();
  const { mutateAsync: updateEmployee } = useUpdateEmployee();

  return {
    createEmployee,
    updateEmployee,
  };
};
